/**
 * StaffSection.tsx
 * PUスタッフ/DOスタッフ（二段組上段）
 * 貸出スタッフ/返却スタッフ（二段組下段）
 * ラベルボタン表示、クリックでモーダル
 * PU/DOはスポットスタッフ付き
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import StaffSelectModal from '../modals/StaffSelectModal';
import {
  fetchStaffList,
  mergeStaffPriorities,
  onStaffPriorityChange,
} from '../../../api/dispatchService';
import { useLineChat } from '../../../hooks/useLineChat';

interface StaffMember {
  id: number | string;
  name?: string;
  staff_name?: string;
  bg_color?: string;
  text_color?: string;
  active?: number;
  is_deleted?: boolean;
  [key: string]: any;
}

interface StaffModalState {
  open: boolean;
  field: string;
  title: string;
  showSpot: boolean;
}

export default function StaffSection({
  data,
  onChange,
  disabled,
}: SectionProps) {
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [modal, setModal] = useState<StaffModalState>({
    open: false, field: '', title: '', showSpot: false,
  });

  // スタッフ一覧
  // - mergeStaffPriorities: localStorage の優先度を API レスポンスにマージ
  //   （固定スタッフ staff/スタッフ/タイミー の +1000 ボーナスも含む）
  // - priority 降順ソート: 出勤者を上に
  // - onStaffPriorityChange: 他タブ／デバイスからの優先度更新もリアクティブに反映
  useEffect(() => {
    let cancelled = false;
    const applyMergeAndSort = (list: StaffMember[]): StaffMember[] => {
      const merged = mergeStaffPriorities(list);
      merged.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
      return merged;
    };

    fetchStaffList({ per_page: 100 })
      .then((list) => {
        if (!cancelled && Array.isArray(list)) {
          setStaffList(applyMergeAndSort(list));
        }
      })
      .catch(() => {});

    const cleanup = onStaffPriorityChange(() => {
      setStaffList((prev) => (prev.length === 0 ? prev : applyMergeAndSort(prev)));
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  // LINE ユーザー（スポットスタッフ用）
  const { users: lineUsers } = useLineChat();

  // DayListと同じロジックでスポットスタッフを抽出
  const pinnedStaffUsers = useMemo(() => {
    if (!Array.isArray(lineUsers)) return [];
    return lineUsers
      .filter((user: any) => {
        if (!user.is_pinned) return false;
        const rids = user.reservation_ids;
        if (!rids) return false;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map((s: string) => s.trim());
        return idArray.some((rid: string) => String(rid).startsWith('STAFF:'));
      })
      .map((user: any) => {
        const rids = user.reservation_ids;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map((s: string) => s.trim());
        const staffId = idArray.find((id: string) => String(id).startsWith('STAFF:'));
        const staffName = staffId?.replace('STAFF:', '') || user.line_display_name;
        return { ...user, staffName };
      });
  }, [lineUsers]);

  // スタッフID→表示名
  const getStaffDisplay = useCallback(
    (staffId: any): string => {
      if (!staffId) return '未指定';
      const found = staffList.find((s) => String(s.id) === String(staffId));
      return found?.staff_name || found?.name || String(staffId);
    },
    [staffList],
  );

  // スタッフIDから色を取得
  const getStaffColor = useCallback(
    (staffId: any): { bg: string; text: string } => {
      if (!staffId) return { bg: '#f1f5f9', text: '#64748b' };
      const found = staffList.find((s) => String(s.id) === String(staffId));
      if (found?.bg_color) return { bg: found.bg_color, text: found.text_color || '#fff' };
      return { bg: '#e2e8f0', text: '#334155' };
    },
    [staffList],
  );

  const openModal = useCallback(
    (field: string, title: string, showSpot: boolean) => {
      if (disabled) return;
      setModal({ open: true, field, title, showSpot });
    },
    [disabled],
  );

  const handleSelect = useCallback(
    (staffId: string | number | null) => {
      onChange?.({ [modal.field]: staffId });
      setModal({ open: false, field: '', title: '', showSpot: false });
    },
    [modal.field, onChange],
  );

  const closeModal = useCallback(() => {
    setModal({ open: false, field: '', title: '', showSpot: false });
  }, []);

  // ラベルボタンレンダリング
  const renderStaffButton = (field: string, label: string, showSpot: boolean) => {
    const staffId = (data as any)[field];
    const displayName = getStaffDisplay(staffId);
    const { bg, text } = getStaffColor(staffId);

    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: 2 }}>
          {label}
        </label>
        <button
          type="button"
          onClick={() => openModal(field, label, showSpot)}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '6px 10px',
            fontSize: '0.8rem',
            fontWeight: 600,
            border: staffId ? 'none' : '1px dashed #cbd5e1',
            borderRadius: 6,
            cursor: disabled ? 'default' : 'pointer',
            background: bg,
            color: text,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName}
        </button>
      </div>
    );
  };

  return (
    <>
      <SectionHeader title="スタッフ" />
      {/* 上段: PU / DO */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {renderStaffButton('pu_staff_id', 'PUスタッフ', true)}
        {renderStaffButton('do_staff_id', 'DOスタッフ', true)}
      </div>
      {/* 下段: 貸出 / 返却 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {renderStaffButton('rent_staff_id', '貸出スタッフ', false)}
        {renderStaffButton('return_staff_id', '返却スタッフ', false)}
      </div>

      <StaffSelectModal
        open={modal.open}
        title={modal.title}
        staffList={staffList}
        currentStaffId={(data as any)[modal.field]}
        onSelect={handleSelect}
        onClose={closeModal}
        pinnedStaffUsers={modal.showSpot ? pinnedStaffUsers : []}
      />
    </>
  );
}
